import * as React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';
import { RootState } from '../reducers';
import { Popover } from 'antd';
import { ReactComponent as Hover } from '../assets/hover.svg';
import { ReactComponent as DownloadIcon } from '../assets/download.svg';
import { ReactComponent as LoadingSpinner } from '../assets/loading-spinner.svg';
import {
  downloadSuspensionLogs,
  getAllSuspensions,
  getCustomerSuspensions,
  getEntitySuspensionDetails,
} from '../actions/orderActions';
import { ENTITY_TYPE, getActorRoleString, isRoleTitan } from './utils';
import { suspensionListInfoType } from '../model/suspensionList';
import { TooltipPlacement } from 'antd/lib/tooltip';
import {
  FormattedMessage,
  injectIntl,
  WrappedComponentProps,
} from 'react-intl';
import { downloadFromUrl } from '../utils/helper';
import DOMPurify from 'dompurify';
import { toast } from 'react-toastify';
import { entitySuspensionsSelector } from '../selectors/suspensionSelectors';
import {
  bundleDetailsSelector,
  orderDetailsSelector,
} from '../selectors/bundleSelectors';
import { getLatestOrderOfOrderType } from '../helpers/product.helpers';
import { ORDER_TYPES } from '../helpers/constants';
import { authTokenSelector } from '../selectors/userSelectors';
import { downloadSuspensionLogFile } from '../service/orderService';
import CustomButton from './Common/Button/Button';

interface SuspensionPopoverProps {
  token: string;
  userRole: string;
  orderType: (typeof ORDER_TYPES)[keyof typeof ORDER_TYPES];
  suspensionListInfo: suspensionListInfoType;
  downloadSuspensionLogs: (
    entityType: string,
    entityID: number,
    fileIDs: string[],
    authToken: string
  ) => Promise<any>;
  shouldShowUnsuspendOption: boolean | null;
  handleUnsuspendClick: () => void;
  hoverIconCss: string;
}

interface ParentProps {
  entityType: ENTITY_TYPE;
  accountId: number | null;
  customerEmail: string | null;
}

type Props = SuspensionPopoverProps & ParentProps & WrappedComponentProps;

export interface SubCategory {
  displayName?: string;
  subCategory: string;
  resolutions: string[];
}

export interface AbuseDetails {
  subCategories: SubCategory[];
  s3Path?: string;
  reason?: string;
}

export interface Metadata {
  abuseDetails?: AbuseDetails;
}

export interface Suspension {
  role: string;
  userEmail: string;
  notes: string[];
  suspensionType: string;
  metadata?: Metadata;
}

const SuspensionPopover: React.FC<Props> = (props) => {
  const [visible, setVisible] = useState(false);
  const [loadingLinks, setLoadingLinks] = useState<Record<string, boolean>>({});
  const [isDetailsFetching, setIsDetailsFetching] = useState<boolean>(false);
  const [suspensionsList, setSuspensionsList] = useState<Suspension[] | null>(
    null
  );
  const [hasFetched, setHasFetched] = useState<boolean>(false);

  const dispatch = useDispatch();
  const entitySuspensions = useSelector(entitySuspensionsSelector);
  const { customer, id: bundleId } = useSelector(bundleDetailsSelector);
  const orders = useSelector(orderDetailsSelector);
  const token = useSelector(authTokenSelector);

  // API call if the data isn't available
  const fetchSuspensionsFromApi = useCallback(async () => {
    // Only check for non-CUSTOMER/ACCOUNT types (like ORDER)
    if (
      props.entityType !== ENTITY_TYPE.CUSTOMER &&
      props.entityType !== ENTITY_TYPE.ACCOUNT
    ) {
      const entityId = getLatestOrderOfOrderType(orders, props.orderType)?.id;
      if (entityId) {
        // Get any suspensions that exist for this entity type
        const existingSuspensions =
          entitySuspensions[props.entityType.toUpperCase()]?.suspensions || [];
        // Check if suspensions for the current entityId exist
        const hasSuspensionForCurrentEntity = existingSuspensions.some(
          (suspension: { entityId: any }) => suspension.entityId === entityId
        );
        if (!hasSuspensionForCurrentEntity) {
          let entityIds = [entityId];
          setIsDetailsFetching(true);
          try {
            await dispatch(
              getEntitySuspensionDetails(
                entityIds,
                props.entityType.toUpperCase(),
                token
              )
            );
          } catch (error) {
            console.error(error);
          } finally {
            setIsDetailsFetching(false);
          }
        }
      }
    }
  }, [
    dispatch,
    orders,
    props.entityType,
    props.orderType,
    token,
    entitySuspensions,
  ]);

  // Build the suspensions list from the store and update local state
  const fetchSuspensionList = useCallback(() => {
    let id: number | null = null;
    if (props.entityType === ENTITY_TYPE.CUSTOMER) {
      id = customer.id;
    } else if (props.entityType === ENTITY_TYPE.ACCOUNT) {
      id = props.accountId;
    } else {
      if (props.orderType) {
        id = getLatestOrderOfOrderType(orders, props.orderType)?.id;
      }
    }

    if (id) {
      const list = entitySuspensions[
        props.entityType.toUpperCase()
      ]?.suspensions.filter(
        (suspension: { entityId: number }) => suspension.entityId === id
      );
      setSuspensionsList(list || []);
    } else {
      setSuspensionsList([]);
    }
  }, [
    props.entityType,
    props.orderType,
    props.accountId,
    customer,
    orders,
    entitySuspensions,
  ]);

  // When the popover becomes visible, check if data is available. If not, call the API.
  useEffect(() => {
    if (visible) {
      // Only fetch from API if it hasn't been done already.
      if (!hasFetched) {
        fetchSuspensionsFromApi().then(() => {
          fetchSuspensionList();
          setHasFetched(true);
        });
      } else {
        fetchSuspensionList();
      }
    }
  }, [visible, fetchSuspensionsFromApi, fetchSuspensionList, hasFetched]);

  // Unsuspend click handler
  const unsuspendClick = useCallback(() => {
    setVisible(false);
    props.handleUnsuspendClick();
  }, [props]);

  // Download logs click handler remains similar
  const handleDownloadClick = useCallback(
    async (s3Path: string) => {
      setLoadingLinks((prev) => ({ ...prev, [s3Path]: true }));

      try {
        let entityId: number | null = null;
        let entityType: string = '';

        switch (props.entityType) {
          case ENTITY_TYPE.ORDER:
            const orderId = getLatestOrderOfOrderType(
              orders,
              props.orderType
            )?.id;
            if (!orderId) {
              toast.error('No order ID available for downloading logs.');
              return;
            }
            entityId = getLatestOrderOfOrderType(orders, props.orderType)?.id;
            entityType = 'Domain';
            break;
          case ENTITY_TYPE.CUSTOMER:
            if (!customer.id) {
              toast.error('No customer ID available for downloading logs.');
              return;
            }
            entityId = customer.id;
            entityType = 'Customer';
            break;
          case ENTITY_TYPE.ACCOUNT:
            if (!props.accountId) {
              toast.error('No accountId available for downloading logs.');
              return;
            }
            entityId = props.accountId;
            entityType = 'Account';
            break;
          default:
            toast.error('Invalid Entity type for downloading logs.');
        }

        const responseData = await downloadSuspensionLogFile(
          entityType,
          entityId,
          [s3Path],
          props.token
        );
        if (responseData && responseData.files[s3Path]) {
          const downloadUrl = responseData.files[s3Path];
          if (downloadUrl) {
            downloadFromUrl(downloadUrl, 'suspension_logs.csv');
          }
        }
      } catch (error) {
        toast.info((error as Error).message);
      } finally {
        setLoadingLinks((prev) => ({ ...prev, [s3Path]: false }));
      }
    },
    [props, bundleId]
  );

  const isTitanUser = isRoleTitan(props.userRole);

  // Synchronously compute the content based on current state
  const content = useMemo(() => {
    if (isDetailsFetching || suspensionsList === null) {
      return (
        <div className="suspension-popover-empty-container">
          <div className="loader"></div>
        </div>
      );
    }

    if (suspensionsList.length === 0) {
      return (
        <div>
          <div className="suspension-popover-error-text">
            <FormattedMessage id="unable-to-fetch-suspensions" />
          </div>
          <button
            className="button button-primary suspension-popover-retry-button"
            onClick={fetchSuspensionList}
          >
            <FormattedMessage id="retry" />
          </button>
        </div>
      );
    }

    return (
      <div className="suspension-popover-container">
        <div>
          <div className="suspension-popover-heading">
            {props.entityType === ENTITY_TYPE.CUSTOMER ? (
              <FormattedMessage id="suspension-popover-heading-customer-suspensions" />
            ) : (
              <FormattedMessage id="suspension-popover-heading-suspensions" />
            )}
            {props.shouldShowUnsuspendOption && (
              <div
                className="suspension-popover-unsuspend-option"
                onClick={unsuspendClick}
              >
                <FormattedMessage id="unsuspend" />
              </div>
            )}
          </div>
          <div className="suspension-popover-table scrollbar">
            <div className="suspension-table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th className="cell">
                      <FormattedMessage id="user" />
                    </th>
                    {isTitanUser && (
                      <th className="cell">
                        <FormattedMessage id="reason" />
                      </th>
                    )}
                    <th className="cell">
                      <FormattedMessage id="category" />
                    </th>
                    <th className="cell">
                      <FormattedMessage id="sub-category" />
                    </th>
                    <th className="cell">
                      <FormattedMessage id="resolution" />
                    </th>
                    <th className="cell">
                      <FormattedMessage id="logs" />
                    </th>
                  </tr>
                </thead>
                <tbody className="suspension-table-body">
                  {suspensionsList.map(
                    (suspension: Suspension, index: number) => {
                      const subCategories =
                        suspension.metadata?.abuseDetails?.subCategories || [];
                      const rowSpanValue =
                        subCategories.length > 0 ? subCategories.length : 1;
                      const s3Path = suspension.metadata?.abuseDetails?.s3Path;
                      const category =
                        suspension.metadata?.abuseDetails?.reason ||
                        suspension.suspensionType ||
                        'Abuse';

                      if (subCategories.length > 0) {
                        return subCategories.map((subCategory, subIndex) => (
                          <React.Fragment key={`${index}-${subIndex}`}>
                            <tr>
                              {subIndex === 0 && (
                                <>
                                  <td rowSpan={rowSpanValue} className="cell">
                                    <div>
                                      {getActorRoleString(
                                        suspension.role,
                                        props.userRole
                                      )}
                                    </div>
                                    <div>{suspension.userEmail}</div>
                                  </td>
                                  {isTitanUser && (
                                    <td rowSpan={rowSpanValue} className="cell">
                                      {suspension.notes.join(', ')}
                                    </td>
                                  )}
                                  <td rowSpan={rowSpanValue} className="cell">
                                    {category}
                                  </td>
                                </>
                              )}
                              <td className="cell">
                                {subCategory.displayName ||
                                  subCategory.subCategory}
                              </td>
                              <td className="cell">
                                <ul className="bullet-list">
                                  {subCategory.resolutions &&
                                  subCategory.resolutions.length > 0 ? (
                                    subCategory.resolutions.map(
                                      (resolution, resIndex) => {
                                        const sanitizedHTML =
                                          DOMPurify.sanitize(resolution, {
                                            ADD_ATTR: ['target'],
                                          });
                                        return (
                                          <li
                                            key={resIndex}
                                            dangerouslySetInnerHTML={{
                                              __html: sanitizedHTML,
                                            }}
                                          />
                                        );
                                      }
                                    )
                                  ) : (
                                    <li>
                                      <FormattedMessage id="contact-titan-support" />
                                    </li>
                                  )}
                                </ul>
                              </td>
                              {subIndex === 0 && (
                                <td
                                  rowSpan={rowSpanValue}
                                  className="cell-large"
                                >
                                  {s3Path ? (
                                    <>
                                      <CustomButton
                                        onClick={() =>
                                          handleDownloadClick(s3Path)
                                        }
                                        // isLoading={loadingLinks[s3Path]}
                                        disabled={loadingLinks[s3Path]}
                                        className={`download-logs`}
                                        type="link"
                                      >
                                        <>
                                          {loadingLinks[s3Path] ? (
                                            <LoadingSpinner />
                                          ) : (
                                            <DownloadIcon />
                                          )}
                                          <FormattedMessage id="download_logs" />
                                        </>
                                      </CustomButton>
                                    </>
                                  ) : (
                                    '-'
                                  )}
                                </td>
                              )}
                            </tr>
                          </React.Fragment>
                        ));
                      } else {
                        return (
                          <tr key={index}>
                            <td className="cell">
                              <div>
                                {getActorRoleString(
                                  suspension.role,
                                  props.userRole
                                )}
                              </div>
                              <div>{suspension.userEmail}</div>
                            </td>
                            {isTitanUser && (
                              <td className="cell">
                                {suspension.notes.join(', ')}
                              </td>
                            )}
                            <td className="cell">{category}</td>
                            <td className="cell">-</td>
                            <td className="cell">
                              <FormattedMessage id="contact-titan-support" />
                            </td>
                            <td className="cell-large">
                              {s3Path ? (
                                <>
                                  <CustomButton
                                    onClick={() => handleDownloadClick(s3Path)}
                                    disabled={loadingLinks[s3Path]}
                                    className={`download-logs`}
                                    type="link"
                                  >
                                    <>
                                      {loadingLinks[s3Path] ? (
                                        <LoadingSpinner />
                                      ) : (
                                        <DownloadIcon />
                                      )}
                                      <FormattedMessage id="download_logs" />
                                    </>
                                  </CustomButton>
                                </>
                              ) : (
                                '-'
                              )}
                            </td>
                          </tr>
                        );
                      }
                    }
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    );
  }, [
    isDetailsFetching,
    suspensionsList,
    props,
    isTitanUser,
    loadingLinks,
    handleDownloadClick,
    unsuspendClick,
    fetchSuspensionList,
  ]);

  // Update popover visibility. Notice we removed API calls from here.
  const handlePopoverVisibilityChange = useCallback((newVisible: boolean) => {
    setVisible(newVisible);
  }, []);

  // Determine tooltip placement based on entity type
  const tooltipPlacement: TooltipPlacement =
    props.entityType === ENTITY_TYPE.ACCOUNT ? 'left' : 'right';

  return (
    <div>
      <Popover
        placement={tooltipPlacement}
        content={content}
        trigger="hover"
        visible={visible}
        onVisibleChange={handlePopoverVisibilityChange}
      >
        <Hover className={props.hoverIconCss} />
      </Popover>
    </div>
  );
};

export default connect(
  (state: RootState) => ({
    token: state.auth.token,
    userRole: state.auth.userRole,
    // suspensionListInfo: state.orders.suspensionListInfo,
    // customerIdRegionDetails: state.orders.customerIdRegionDetails,
  }),
  (dispatch) =>
    bindActionCreators(
      { getAllSuspensions, getCustomerSuspensions, downloadSuspensionLogs },
      dispatch
    )
)(injectIntl(SuspensionPopover));
